'use strict';

const libPictView = require('pict-view');
const libMarkdownRender = require('./MarkdownRender.js');

/**
 * RoutineSteps — the ordered step editor embedded on a Routine's detail page
 * (EntityDetail mounts it into #Routine-Steps when the descriptor says
 * Steps: true). Each step is a card: title, a markdown body with an
 * Edit/Preview toggle (paste or drop an image straight into the textarea to
 * upload it), an optional expected result, and a media strip (screenshots and
 * videos, Media OwnerType 'RoutineStep'). Steps reorder with up/down buttons;
 * a move renumbers SortOrder for every step that shifted, so the order is
 * always clean. Field edits save on change (blur); there is no save button.
 *
 * Read-only mode (no content.edit) renders the same cards with rendered
 * bodies and no inputs.
 */
class PictViewRoutineSteps extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		this._IDRoutine = 0;
		this._Editable = false;
		this._LoadToken = 0;
		this._raw = [];      // RoutineStep rows, sorted by SortOrder
		this._media = {};    // IDRoutineStep -> Media rows
		this._modes = {};    // IDRoutineStep -> 'edit' | 'preview'
		this.pict.AppData.RoutineSteps = this.pict.AppData.RoutineSteps ||
			{ IDRoutine: 0, Editable: false, Steps: [], EmptySlot: [], AddSlot: [] };
	}

	_stepAPI() { return this.pict.providers.RoutineStepAPI; }
	_mediaAPI() { return this.pict.providers.MediaAPI; }

	// ── entry + load ─────────────────────────────────────────────────────────
	show(pIDRoutine, pEditable)
	{
		this._IDRoutine = Number(pIDRoutine) || 0;
		this._Editable = !!pEditable;
		this._modes = {};
		this._load();
	}

	_load()
	{
		let tmpToken = ++this._LoadToken;
		if (!this._IDRoutine || !this._stepAPI()) { return; }
		this._stepAPI().listForRoutine(this._IDRoutine)
			.then((pSteps) =>
			{
				if (tmpToken !== this._LoadToken) { return null; }
				this._raw = pSteps || [];
				return Promise.all(this._raw.map((pStep) =>
					this._mediaAPI().listForOwner('RoutineStep', pStep.IDRoutineStep)
						.then((pRows) => { this._media[pStep.IDRoutineStep] = pRows || []; })));
			})
			.then(() =>
			{
				if (tmpToken !== this._LoadToken) { return; }
				this._rebuild();
				this.render();
			});
	}

	// Refresh one step's media strip without refetching every step.
	_loadMediaFor(pID)
	{
		this._mediaAPI().listForOwner('RoutineStep', pID)
			.then((pRows) => { this._media[pID] = pRows || []; this._rebuild(); this.render(); });
	}

	_rawStep(pID) { return this._raw.find((pStep) => String(pStep.IDRoutineStep) === String(pID)) || null; }

	// ── build the AppData model from the raw caches (no fetch) ──────────────
	_rebuild()
	{
		let tmpState = this.pict.AppData.RoutineSteps;
		tmpState.IDRoutine = this._IDRoutine;
		tmpState.Editable = this._Editable;
		tmpState.Steps = this._raw.map((pStep, pIdx) =>
		{
			let tmpID = pStep.IDRoutineStep;
			// First open: empty bodies start in edit mode (type immediately),
			// written ones start in preview (read the routine comfortably).
			if (!this._modes[tmpID]) { this._modes[tmpID] = (this._Editable && !String(pStep.Body || '').trim()) ? 'edit' : 'preview'; }
			let tmpMode = this._Editable ? this._modes[tmpID] : 'preview';
			let tmpMedia = (this._media[tmpID] || []).map((pM) =>
			{
				let tmpIsImage = (String(pM.MimeType || '').indexOf('image/') === 0) || (pM.MediaType === 'Screenshot');
				let tmpIsVideo = String(pM.MimeType || '').indexOf('video/') === 0;
				let tmpURL = '/1.0/Media/' + pM.IDMedia + '/Blob';
				let tmpTitle = pM.Title || pM.OriginalFileName || ('Media #' + pM.IDMedia);
				return (
				{
					ID: pM.IDMedia, StepID: tmpID,
					ImageSlot: tmpIsImage ? [{ URL: tmpURL, Title: tmpTitle }] : [],
					VideoSlot: tmpIsVideo ? [{ URL: tmpURL, Title: tmpTitle }] : [],
					FileSlot: (tmpIsImage || tmpIsVideo) ? [] : [{ URL: tmpURL, Title: tmpTitle, Type: pM.MediaType || 'file' }],
					RemoveSlot: this._Editable ? [{ ID: pM.IDMedia, StepID: tmpID }] : []
				});
			});
			let tmpExpected = String(pStep.ExpectedResult || '');
			return (
			{
				ID: tmpID,
				Idx: pIdx + 1,
				Title: String(pStep.Title || ''),
				Body: String(pStep.Body || ''),
				Expected: tmpExpected,
				BodyContainerID: 'RS-Body-' + tmpID,
				HeadEditSlot: this._Editable ? [{
					ID: tmpID, Idx: pIdx + 1, Title: String(pStep.Title || ''),
					UpSlot: (pIdx > 0) ? [{ ID: tmpID }] : [],
					DownSlot: (pIdx < this._raw.length - 1) ? [{ ID: tmpID }] : [],
					ToggleLabel: (tmpMode === 'edit') ? 'Preview' : 'Edit'
				}] : [],
				HeadViewSlot: this._Editable ? [] : [{ Idx: pIdx + 1, Title: String(pStep.Title || '') }],
				BodyEditSlot: (this._Editable && tmpMode === 'edit') ? [{ ID: tmpID, Body: String(pStep.Body || '') }] : [],
				BodyPreviewSlot: (tmpMode === 'preview') ? [{ ID: tmpID, ContainerID: 'RS-Body-' + tmpID }] : [],
				ExpectedEditSlot: this._Editable ? [{ ID: tmpID, Expected: tmpExpected }] : [],
				ExpectedViewSlot: (!this._Editable && tmpExpected) ? [{ Expected: tmpExpected }] : [],
				Media: tmpMedia,
				UploadSlot: this._Editable ? [{ ID: tmpID }] : []
			});
		});
		tmpState.EmptySlot = tmpState.Steps.length ? [] : [{}];
		tmpState.AddSlot = this._Editable ? [{}] : [];
	}

	// ── field edits (oninput -> raw cache; onchange -> save) ────────────────
	setTitle(pID, pValue) { let tmpStep = this._rawStep(pID); if (tmpStep) { tmpStep.Title = pValue; } }
	setBody(pID, pValue) { let tmpStep = this._rawStep(pID); if (tmpStep) { tmpStep.Body = pValue; } }
	setExpected(pID, pValue) { let tmpStep = this._rawStep(pID); if (tmpStep) { tmpStep.ExpectedResult = pValue; } }

	saveStep(pID)
	{
		let tmpStep = this._rawStep(pID);
		if (!tmpStep) { return; }
		this._stepAPI().update(pID, { Title: tmpStep.Title || '', Body: tmpStep.Body || '', ExpectedResult: tmpStep.ExpectedResult || '' })
			.catch((pErr) => this._toast('Step save failed: ' + (pErr.message || pErr), 'error'));
	}

	togglePreview(pID)
	{
		this._modes[pID] = (this._modes[pID] === 'edit') ? 'preview' : 'edit';
		this._rebuild();
		this.render();
	}

	// ── structure edits ──────────────────────────────────────────────────────
	addStep()
	{
		let tmpMax = this._raw.reduce((pMax, pStep) => Math.max(pMax, Number(pStep.SortOrder) || 0), 0);
		this._stepAPI().create(
			{
				IDRoutine: this._IDRoutine,
				Title: 'Step ' + (this._raw.length + 1),
				Body: '',
				ExpectedResult: '',
				SortOrder: tmpMax + 10
			})
			.then(() => this._load())
			.catch((pErr) => this._toast('Could not add a step: ' + (pErr.message || pErr), 'error'));
	}

	moveUp(pID) { this._move(pID, -1); }
	moveDown(pID) { this._move(pID, 1); }

	// Reorder by renumbering: shift the step in the in-memory order, then persist
	// SortOrder = (index+1)*10 for every step whose number changed. Self-healing
	// for duplicate SortOrders.
	_move(pID, pDelta)
	{
		let tmpIdx = this._raw.findIndex((pStep) => String(pStep.IDRoutineStep) === String(pID));
		let tmpNewIdx = tmpIdx + pDelta;
		if (tmpIdx < 0 || tmpNewIdx < 0 || tmpNewIdx >= this._raw.length) { return; }
		let tmpOrder = this._raw.slice();
		tmpOrder.splice(tmpIdx, 1);
		tmpOrder.splice(tmpNewIdx, 0, this._raw[tmpIdx]);
		let tmpWrites = [];
		for (let i = 0; i < tmpOrder.length; i++)
		{
			let tmpWanted = (i + 1) * 10;
			if ((Number(tmpOrder[i].SortOrder) || 0) !== tmpWanted)
			{
				tmpOrder[i].SortOrder = tmpWanted;
				tmpWrites.push(this._stepAPI().update(tmpOrder[i].IDRoutineStep, { SortOrder: tmpWanted }));
			}
		}
		this._raw = tmpOrder;
		this._rebuild();
		this.render();
		Promise.all(tmpWrites).catch((pErr) => { this._toast('Reorder failed: ' + (pErr.message || pErr), 'error'); this._load(); });
	}

	removeStep(pID)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		let tmpDo = () => this._stepAPI().remove(pID)
			.then(() => this._load())
			.catch((pErr) => this._toast('Delete failed: ' + (pErr.message || pErr), 'error'));
		if (tmpModal && typeof tmpModal.confirm === 'function')
		{
			tmpModal.confirm('Delete this step?', { dangerous: true }).then((pOk) => { if (pOk) tmpDo(); });
		}
		else { tmpDo(); }
	}

	// ── media ────────────────────────────────────────────────────────────────
	uploadMedia(pID, pFiles)
	{
		let tmpFiles = Array.prototype.slice.call(pFiles || []);
		if (!tmpFiles.length) { return; }
		this._toast('Uploading ' + tmpFiles.length + ' file' + (tmpFiles.length > 1 ? 's' : '') + '…', 'info');
		Promise.all(tmpFiles.map((pFile) => this._mediaAPI().upload('RoutineStep', pID, pFile)))
			.then(() => { this._toast('Uploaded.', 'success'); this._loadMediaFor(pID); })
			.catch((pErr) => this._toast('Upload failed: ' + (pErr.message || pErr), 'error'));
	}

	removeMedia(pID, pIDMedia)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		let tmpDo = () => this._mediaAPI().remove(pIDMedia)
			.then(() => this._loadMediaFor(pID))
			.catch((pErr) => this._toast('Remove failed: ' + (pErr.message || pErr), 'error'));
		if (tmpModal && typeof tmpModal.confirm === 'function')
		{
			tmpModal.confirm('Remove this media?', { dangerous: true }).then((pOk) => { if (pOk) tmpDo(); });
		}
		else { tmpDo(); }
	}

	// Paste or drop a file onto a step's body textarea: upload it against the
	// step and insert a markdown reference at the cursor.
	bodyPaste(pEvent, pTextarea, pID)
	{
		let tmpFiles = (pEvent && pEvent.clipboardData && pEvent.clipboardData.files) || [];
		if (!tmpFiles.length) { return; }
		pEvent.preventDefault();
		this._uploadIntoBody(pTextarea, pID, tmpFiles);
	}

	bodyDrop(pEvent, pTextarea, pID)
	{
		let tmpFiles = (pEvent && pEvent.dataTransfer && pEvent.dataTransfer.files) || [];
		if (!tmpFiles.length) { return; }
		pEvent.preventDefault();
		this._uploadIntoBody(pTextarea, pID, tmpFiles);
	}

	_uploadIntoBody(pTextarea, pID, pFiles)
	{
		let tmpFile = pFiles[0];
		if (!tmpFile) { return; }
		this._toast('Uploading ' + (tmpFile.name || 'file') + '…', 'info');
		this._mediaAPI().upload('RoutineStep', pID, tmpFile)
			.then((pRow) =>
			{
				let tmpURL = '/1.0/Media/' + pRow.IDMedia + '/Blob';
				let tmpIsImage = String(tmpFile.type || '').indexOf('image/') === 0;
				let tmpName = tmpFile.name || (tmpIsImage ? 'image' : 'file');
				let tmpSnippet = (tmpIsImage ? ('![' + tmpName + '](' + tmpURL + ')') : ('[' + tmpName + '](' + tmpURL + ')'));
				let tmpStart = (pTextarea && typeof pTextarea.selectionStart === 'number') ? pTextarea.selectionStart : ((pTextarea && pTextarea.value) || '').length;
				if (pTextarea)
				{
					pTextarea.value = pTextarea.value.slice(0, tmpStart) + '\n' + tmpSnippet + '\n' + pTextarea.value.slice(tmpStart);
					this.setBody(pID, pTextarea.value);
				}
				this.saveStep(pID);
				this._toast('Uploaded.', 'success');
				this._loadMediaFor(pID);
			})
			.catch((pErr) => this._toast('Upload failed: ' + (pErr.message || pErr), 'error'));
	}

	_toast(pMessage, pType)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (tmpModal && typeof tmpModal.toast === 'function')
		{
			tmpModal.toast(pMessage, { type: pType || 'info', duration: (pType === 'error') ? 6000 : 2500 });
		}
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		if (this.pict.CSSMap) { this.pict.CSSMap.injectCSS(); }
		// Render markdown bodies for the steps in preview mode.
		if (typeof document !== 'undefined')
		{
			let tmpSteps = (this.pict.AppData.RoutineSteps && this.pict.AppData.RoutineSteps.Steps) || [];
			for (let i = 0; i < tmpSteps.length; i++)
			{
				if (!tmpSteps[i].BodyPreviewSlot.length) { continue; }
				let tmpHost = document.getElementById(tmpSteps[i].BodyContainerID);
				if (!tmpHost) { continue; }
				libMarkdownRender.renderInto(this.pict.providers.Content, tmpHost, tmpSteps[i].Body,
					'<span class="ps-rel-empty">Nothing written for this step yet.</span>');
			}
		}
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}
}

module.exports = PictViewRoutineSteps;

module.exports.default_configuration =
{
	ViewIdentifier: 'Plansheet-RoutineSteps',
	DefaultRenderable: 'RoutineSteps-Container',
	AutoRender: false,
	CSS: /*css*/`
		.ps-steps { display: flex; flex-direction: column; gap: 12px; }
		.ps-step-card {
			background: var(--theme-color-background-panel, #fff);
			border: 1px solid var(--theme-color-border-default, #e6e9ee);
			border-radius: 10px; padding: 14px 16px;
		}
		.ps-step-head { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
		.ps-step-num {
			flex: 0 0 auto; width: 26px; height: 26px; border-radius: 999px; display: inline-flex;
			align-items: center; justify-content: center; font-size: 13px; font-weight: 600;
			background: var(--theme-color-brand-primary, #2988df); color: var(--theme-color-text-on-brand, #fff);
		}
		.ps-step-title-input { flex: 1; font-weight: 600; }
		.ps-step-title-view { flex: 1; font-size: 15px; font-weight: 600; color: var(--theme-color-text-primary, #2c3140); }
		.ps-step-body { width: 100%; min-height: 88px; resize: vertical; font-family: var(--theme-typography-family-mono, monospace); font-size: 13px; box-sizing: border-box; }
		.ps-step-preview { border: 1px dashed transparent; border-radius: 8px; padding: 2px 0; }
		.ps-step-preview img, .ps-step-preview video { max-width: 100%; border-radius: 6px; }
		.ps-step-expected { margin-top: 10px; }
		.ps-step-expected-label { display: block; font-size: 11px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; color: var(--theme-color-text-secondary, #5b6376); margin-bottom: 5px; }
		.ps-step-expected-input { display: block; width: 100%; box-sizing: border-box; min-height: 88px; resize: vertical; line-height: 1.5; }
		.ps-step-expected-text {
			white-space: pre-wrap; word-break: break-word;
			font-size: 13px; line-height: 1.5; color: var(--theme-color-text-primary, #2c3140);
			background: var(--theme-color-background-secondary, #f6f8fa);
			border-left: 3px solid var(--theme-color-brand-primary, #2988df);
			border-radius: 6px; padding: 8px 12px;
		}
		.ps-step-media { margin-top: 10px; }
		.ps-step-foot { display: flex; align-items: center; gap: 8px; margin-top: 10px; }
		.ps-steps-empty { font-size: 13px; color: var(--theme-color-text-secondary, #5b6376); font-style: italic; }
		.ps-steps-add { margin-top: 2px; }
	`,
	Templates:
	[
		{
			Hash: 'RoutineSteps-Container',
			Template: /*html*/`
<div class="ps-steps">
	{~TS:RS-Card:AppData.RoutineSteps.Steps~}
	{~TS:RS-Empty:AppData.RoutineSteps.EmptySlot~}
	{~TS:RS-Add:AppData.RoutineSteps.AddSlot~}
</div>`
		},
		{
			Hash: 'RS-Card',
			Template: /*html*/`
<div class="ps-step-card">
	{~TS:RS-HeadEdit:Record.HeadEditSlot~}
	{~TS:RS-HeadView:Record.HeadViewSlot~}
	{~TS:RS-BodyEdit:Record.BodyEditSlot~}
	{~TS:RS-BodyPreview:Record.BodyPreviewSlot~}
	{~TS:RS-ExpectedEdit:Record.ExpectedEditSlot~}
	{~TS:RS-ExpectedView:Record.ExpectedViewSlot~}
	<div class="ps-media-grid ps-step-media">{~TS:RS-MediaItem:Record.Media~}</div>
	{~TS:RS-Upload:Record.UploadSlot~}
</div>`
		},
		{
			Hash: 'RS-HeadEdit',
			Template: /*html*/`
<div class="ps-step-head">
	<span class="ps-step-num">{~D:Record.Idx~}</span>
	<input type="text" class="ps-input ps-step-title-input" value="{~D:Record.Title~}" placeholder="What to do"
		oninput="_Pict.views['Plansheet-RoutineSteps'].setTitle({~D:Record.ID~}, this.value)"
		onchange="_Pict.views['Plansheet-RoutineSteps'].saveStep({~D:Record.ID~})">
	<button class="ps-btn ps-btn-sm" onclick="_Pict.views['Plansheet-RoutineSteps'].togglePreview({~D:Record.ID~})">{~D:Record.ToggleLabel~}</button>
	{~TS:RS-UpBtn:Record.UpSlot~}
	{~TS:RS-DownBtn:Record.DownSlot~}
	<button class="ps-btn ps-btn-sm ps-btn-danger" title="Delete step" onclick="_Pict.views['Plansheet-RoutineSteps'].removeStep({~D:Record.ID~})">Delete</button>
</div>`
		},
		{ Hash: 'RS-HeadView', Template: /*html*/`<div class="ps-step-head"><span class="ps-step-num">{~D:Record.Idx~}</span><span class="ps-step-title-view">{~D:Record.Title~}</span></div>` },
		{ Hash: 'RS-UpBtn', Template: /*html*/`<button class="ps-btn ps-btn-sm" title="Move up" onclick="_Pict.views['Plansheet-RoutineSteps'].moveUp({~D:Record.ID~})">Up</button>` },
		{ Hash: 'RS-DownBtn', Template: /*html*/`<button class="ps-btn ps-btn-sm" title="Move down" onclick="_Pict.views['Plansheet-RoutineSteps'].moveDown({~D:Record.ID~})">Down</button>` },
		{
			Hash: 'RS-BodyEdit',
			Template: /*html*/`<textarea class="ps-input ps-step-body" placeholder="Markdown. Paste or drop a screenshot to upload it."
	oninput="_Pict.views['Plansheet-RoutineSteps'].setBody({~D:Record.ID~}, this.value)"
	onchange="_Pict.views['Plansheet-RoutineSteps'].saveStep({~D:Record.ID~})"
	onpaste="_Pict.views['Plansheet-RoutineSteps'].bodyPaste(event, this, {~D:Record.ID~})"
	ondragover="event.preventDefault()"
	ondrop="_Pict.views['Plansheet-RoutineSteps'].bodyDrop(event, this, {~D:Record.ID~})">{~D:Record.Body~}</textarea>`
		},
		{ Hash: 'RS-BodyPreview', Template: /*html*/`<div class="ps-step-preview" id="{~D:Record.ContainerID~}"></div>` },
		{
			Hash: 'RS-ExpectedEdit',
			Template: /*html*/`
<div class="ps-step-expected">
	<span class="ps-step-expected-label">Expect</span>
	<textarea class="ps-input ps-step-expected-input" placeholder="Expected result (optional)"
		oninput="_Pict.views['Plansheet-RoutineSteps'].setExpected({~D:Record.ID~}, this.value)"
		onchange="_Pict.views['Plansheet-RoutineSteps'].saveStep({~D:Record.ID~})">{~D:Record.Expected~}</textarea>
</div>`
		},
		{ Hash: 'RS-ExpectedView', Template: /*html*/`<div class="ps-step-expected"><span class="ps-step-expected-label">Expect</span><div class="ps-step-expected-text">{~D:Record.Expected~}</div></div>` },
		{ Hash: 'RS-MediaItem', Template: /*html*/`<div class="ps-media-item">{~TS:RS-MediaImage:Record.ImageSlot~}{~TS:RS-MediaVideo:Record.VideoSlot~}{~TS:RS-MediaFile:Record.FileSlot~}{~TS:RS-MediaRemove:Record.RemoveSlot~}</div>` },
		{ Hash: 'RS-MediaImage', Template: /*html*/`<a href="{~D:Record.URL~}" target="_blank" rel="noopener"><img class="ps-media-thumb" src="{~D:Record.URL~}" alt="{~D:Record.Title~}" title="{~D:Record.Title~}"></a>` },
		{ Hash: 'RS-MediaVideo', Template: /*html*/`<video class="ps-media-video" controls preload="metadata" src="{~D:Record.URL~}" title="{~D:Record.Title~}"></video>` },
		{ Hash: 'RS-MediaFile', Template: /*html*/`<a class="ps-link ps-media-file" href="{~D:Record.URL~}" target="_blank" rel="noopener"><span class="ps-media-file-type">{~D:Record.Type~}</span>{~D:Record.Title~}</a>` },
		{ Hash: 'RS-MediaRemove', Template: /*html*/`<button class="ps-chip-x ps-media-x" title="Remove" onclick="_Pict.views['Plansheet-RoutineSteps'].removeMedia({~D:Record.StepID~},{~D:Record.ID~})">×</button>` },
		{
			Hash: 'RS-Upload',
			Template: /*html*/`<div class="ps-step-foot"><label class="ps-btn ps-btn-sm ps-file-btn">+ Media<input type="file" multiple accept="image/*,video/*" style="display:none" onchange="_Pict.views['Plansheet-RoutineSteps'].uploadMedia({~D:Record.ID~}, this.files)"></label></div>`
		},
		{ Hash: 'RS-Empty', Template: /*html*/`<div class="ps-steps-empty">No steps yet.</div>` },
		{ Hash: 'RS-Add', Template: /*html*/`<div class="ps-steps-add"><button class="ps-btn ps-btn-primary" onclick="_Pict.views['Plansheet-RoutineSteps'].addStep()">+ Add step</button></div>` }
	],
	Renderables:
	[
		{
			RenderableHash: 'RoutineSteps-Container',
			TemplateHash: 'RoutineSteps-Container',
			ContentDestinationAddress: '#Routine-Steps'
		}
	]
};
